import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ErrorBoundary, ProtectedRoute } from './components/common';
import { Layout } from './components/layout';
import { AppProvider } from './contexts/AppContext';
import { AuthProvider } from './contexts/AuthContext';
import { ROUTES, USER_ROLES } from './constants';
import {
  LoginPage,
  OtpVerificationPage,
  DashboardPage,
  MarketersPage,
  MarketerDetailPage,
  MarketerEarningsBonusesPage,
  MarketerEditPersonalPage,
  MarketerEditBankPage,
  MarketerEditPartitaIvaPage,
  MarketerEditCodiceFiscalePage,
  MerchantsPage,
  MerchantDetailPage,
  MerchantEditBillingPage,
  MerchantEditBusinessOwnerPage,
  MerchantEditBusinessDetailsPage,
  ShoppersPage,
  ShopperDetailPage,
  ShopperEarningsBonusesPage,
  ShopperEditPersonalPage,
  ShopperEditBankPage,
  ShopperEditCodiceFiscalePage,
  ShopperPayoutPage,
  ShopperPayoutImportPage,
  ShopperPayoutImportDetailPage,
  UsersPage,
  RolesPage,
  ProfilePage,
  TopupCashbackPage,
  OneSnapTransactionsPage,
  OneSnapTransactionDetailPage,
  ReversalTransactionsPage,
  ReversalTransactionDetailPage,
  MasterAssetsPage,
  ManualPurchasePage,
  ManualPurchaseDetailPage,
  PurchasesPage,
  OAuthManagementPage,
} from './pages';

const NotFound = () => (
  <div className='min-h-screen flex items-center justify-center bg-gray-50'>
    <div className='text-center'>
      <h1 className='text-2xl font-bold text-gray-900'>404 - Page Not Found</h1>
      <p className='text-gray-600'>
        The page you're looking for doesn't exist.
      </p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path={ROUTES.LOGIN} element={<LoginPage />} />
              <Route
                path={ROUTES.OTP_VERIFICATION}
                element={<OtpVerificationPage />}
              />

              {/* Protected routes with layout */}
              <Route
                path={ROUTES.DASHBOARD}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MARKETERS_DETAIL_LIST}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MarketerDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MARKETERS_EARNINGS_BONUSES}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MarketerEarningsBonusesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MARKETERS_EDIT_PERSONAL}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MarketerEditPersonalPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MARKETERS_EDIT_BANK}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MarketerEditBankPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MARKETERS_EDIT_PARTITA_IVA}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MarketerEditPartitaIvaPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MARKETERS_EDIT_CODICE_FISCALE}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MarketerEditCodiceFiscalePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MARKETERS}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MarketersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TOPUP_CASHBACK}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <TopupCashbackPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ONE_SNAP}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <OneSnapTransactionsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ONE_SNAP_DETAIL}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <OneSnapTransactionDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MANUAL_PURCHASE}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ManualPurchasePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.PURCHASES}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <PurchasesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              >
                <Route path=':id' element={<ManualPurchaseDetailPage />} />
              </Route>
              <Route
                path={ROUTES.REVERSAL}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ReversalTransactionsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.REVERSAL_DETAIL}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ReversalTransactionDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MERCHANTS}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MerchantsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MERCHANTS_EDIT_BILLING}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MerchantEditBillingPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MERCHANTS_EDIT_BUSINESS_OWNER}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MerchantEditBusinessOwnerPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MERCHANTS_EDIT_BUSINESS_DETAILS}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MerchantEditBusinessDetailsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MERCHANTS_DETAIL}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <MerchantDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPERS}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ShoppersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPERS_DETAIL}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ShopperDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPERS_EARNINGS_BONUSES}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ShopperEarningsBonusesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPERS_EDIT_PERSONAL}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ShopperEditPersonalPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPERS_EDIT_BANK}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ShopperEditBankPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPERS_EDIT_CODICE_FISCALE}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <ShopperEditCodiceFiscalePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPER_PAYOUT}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ShopperPayoutPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPER_PAYOUT_IMPORT}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ShopperPayoutImportPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SHOPPER_PAYOUT_DETAIL}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ShopperPayoutImportDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.USERS}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UsersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ROLES}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RolesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.PROFILE}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProfilePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MASTER_ASSETS}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <MasterAssetsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.OAUTH_MANAGEMENT}
                element={
                  <ProtectedRoute minRole={USER_ROLES.ADMIN}>
                    <Layout>
                      <OAuthManagementPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Root route */}
              <Route
                path='/'
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Error routes */}
              <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
              <Route path='*' element={<NotFound />} />
            </Routes>
          </Router>
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
